import { useQuery } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import ConsultReport from '@components/ConsultReport';
import HandleQuery from '@components/HandleQuery';
import ExpertJourneyPage from '@components/pageTypes/ExpertJourneyPage';
import useSubscribeToConsult from '@hooks/subscriptions/useSubscribeToConsult';
import useSubscribeToJourney from '@hooks/subscriptions/useSubscribeToJourney';
import useGlobalOpener from '@hooks/useGlobalOpener';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import { OpenerTypeEnum } from '@typing/Enums';
import {
  ConsultReportPublishDocument,
  ConsultReportPublishMutation,
  ConsultReportPublishMutationVariables,
  GetConsultDocument,
  GetConsultQuery,
  GetConsultQueryVariables,
  GetJourneyWithClientRolesDocument,
  GetJourneyWithClientRolesQuery,
  GetJourneyWithClientRolesQueryVariables
} from '@typing/Generated';
import { consultReportPublishable } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  consultId: string;
  journeyId: string;
};

const ExpertJourneyConsultReportPage = ({ consultId, journeyId }: Props) => {
  const intl = useIntl();
  const timeTrackingOpener = useGlobalOpener(OpenerTypeEnum.TIME_LOG);
  const myExpert = useMyExpert();

  const getConsultQuery = useQuery<GetConsultQuery, GetConsultQueryVariables>(GetConsultDocument, {
    variables: { id: consultId }
  });

  useSubscribeToConsult(consultId, () => {
    getConsultQuery.refetch();
  });

  const getJourneyQuery = useQuery<GetJourneyWithClientRolesQuery, GetJourneyWithClientRolesQueryVariables>(
    GetJourneyWithClientRolesDocument,
    {
      variables: { id: journeyId }
    }
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  const [consultReportPublish] = useMutationWithValidation<
    ConsultReportPublishMutation,
    ConsultReportPublishMutationVariables
  >(ConsultReportPublishDocument, 'consultReport');

  const handlePublish = useCallback(() => {
    if (getConsultQuery.data?.consult?.consultReport?.id) {
      consultReportPublish({ id: getConsultQuery.data.consult.consultReport.id }).then(() => timeTrackingOpener.open());
    }
  }, [consultReportPublish, getConsultQuery.data?.consult.consultReport?.id, timeTrackingOpener]);

  return (
    <ExpertJourneyPage gradient={false} helmetKey="pageTitles.expertJourney.consultReport.show" journeyId={journeyId}>
      <HandleQuery query={getConsultQuery}>
        {data => (
          <HandleQuery query={getJourneyQuery}>
            {journeyData => (
              <>
                <div className="align-right">
                  {data?.consult.consultReport && !data?.consult.consultReport?.published && (
                    <SecondaryButton
                      disabled={!consultReportPublishable(data.consult, journeyData.journey.careProfile)}
                      onClick={handlePublish}
                      textKey="models.consultReport.publishReport"
                      title={
                        consultReportPublishable(data.consult, journeyData.journey.careProfile)
                          ? ''
                          : intl.formatMessage({ id: 'models.consultReport.unpublishableReason' })
                      }
                    />
                  )}
                  <PrimaryButton
                    icon="edit"
                    routerLink={journeyRoute({
                      journeyId: journeyData.journey.id,
                      linkAfterJourney: `/consults/${consultId}/consult-report/edit`,
                      myExpert
                    })}
                    textKey="dictionary.edit"
                  />
                </div>
                <ConsultReport consult={data.consult} journey={journeyData.journey} />
              </>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertJourneyPage>
  );
};

export default ExpertJourneyConsultReportPage;
